import dayjs, { ConfigType } from "dayjs";
import { LngLatBounds } from "react-map-gl/maplibre";
import { catching } from "shared/utils/fns";
import { z } from "zod";

export const zx = {
    json: () => z.union([z.string().transform((it) => JSON.parse(it)), z.any().refine((it) => typeof it == "object")]),
    date: () => z.preprocess((it) => (typeof it == "string" ? new Date(+it) : it), z.coerce.date()),
    instant: () =>
        z
            .preprocess((it) => catching(() => dayjs(it as ConfigType)) ?? it, z.coerce.date())
            .transform((it) => +it)
            .transform((it) => it.toString()),
    geo: {
        position: () => z.number().array().min(2).max(3) as z.ZodType<GeoJSON.Position>, // [lng, lat] per https://datatracker.ietf.org/doc/html/rfc7946#section-3.1.1
        point: () =>
            zx.json().pipe(z.object({ type: z.literal("Point"), coordinates: zx.geo.position() }) satisfies z.ZodType<GeoJSON.Point>),
        coerce: {
            feature: <G extends GeoJSON.Geometry>(geometry: z.ZodType<G>) =>
                zx
                    .json()
                    .pipe(geometry)
                    .transform((geometry) => ({ type: "Feature", geometry, properties: {} }))
                    .pipe(zx.geo.feature(geometry)),
        },
        polygon: Object.assign(
            () =>
                zx.json().pipe(
                    z.object({
                        type: z.literal("Polygon"),
                        coordinates: zx.geo.position().array().array(),
                    }) satisfies z.ZodType<GeoJSON.Polygon>,
                ),
            {
                coerce: {
                    fromLngLatBounds: () =>
                        z
                            .any()
                            .transform((it: LngLatBounds) => ({
                                type: "Polygon",
                                coordinates: [
                                    [
                                        it.getSouthWest().toArray(),
                                        it.getSouthEast().toArray(),
                                        it.getNorthEast().toArray(),
                                        it.getNorthWest().toArray(),
                                        it.getSouthWest().toArray(),
                                    ],
                                ],
                            }))
                            .pipe(zx.geo.polygon()),
                },
            },
        ),
        multipolygon: Object.assign(
            () =>
                zx.json().pipe(
                    z.object({
                        type: z.literal("MultiPolygon"),
                        coordinates: zx.geo.position().array().array().array(),
                    }) satisfies z.ZodType<GeoJSON.MultiPolygon>,
                ),
            {
                coerce: Object.assign(() => z.union([zx.geo.multipolygon(), zx.geo.multipolygon.coerce.fromPolygon()]), {
                    fromPolygon: () =>
                        zx.geo
                            .polygon()
                            .transform((it) => [it.coordinates])
                            .pipe(zx.geo.multipolygon()),
                }),
            },
        ),
        feature: <G extends GeoJSON.Geometry>(geometry: z.ZodType<G>) =>
            zx.json().pipe(
                z.object({
                    id: z.coerce.string(), // per the official RFC-7946, this 'id' is optional but for our use cases we want to keep it required
                    type: z.literal("Feature"),
                    properties: z.object({}).passthrough().default({}),
                    geometry,
                }) as z.ZodType<GeoJSON.Feature<G>>,
            ),
    },
    lngLat: Object.assign(() => z.tuple([z.number(), z.number()]), {
        coerce: {
            fromPoint: () =>
                zx.geo
                    .point()
                    .transform((it) => it.coordinates)
                    .pipe(zx.lngLat()),
        },
    }),
    lngLatBounds: Object.assign(() => z.tuple([zx.lngLat(), zx.lngLat()]), {
        coerce: {
            fromBBox: (bbox: [number, number, number, number] | [number, number, number, number, number, number]) =>
                zx.lngLatBounds().parse([bbox.slice(0, 2), bbox.slice(2, 4)]),
        },
    }),
};
